import React from 'react';
import propTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Megaphone, ShieldOff } from 'lucide-react';
import CarrierLucideIcon from 'components/CarrierLucideIcon/CarrierLucideIcon';
import { getCompany } from 'helpers/getCompanyVariables';
import { isAgentAppointedWithCarrier } from 'helpers/appointments/appointments';
import {
  OEP_UPDATE_ICON_COLOR,
  OEP_UPDATE_ICON_DESCRIPTION
} from 'helpers/oepUpdates';
import { getSuncareCarrierCardStatus } from 'helpers/suncare/suncare';
import { cn } from 'lib/utils';

const company = getCompany();

const CARRIER_ICON_SIZE = 18;

const PENDING_CLASS = 'bg-pending [&_span]:text-white!';

const STATUS_CLASS = {
  'Action Needed': 'bg-semantic-danger [&_span]:text-white!',
  'Pending Agent Action': 'bg-semantic-danger [&_span]:text-white!',
  Pending: PENDING_CLASS,
  'In progress': PENDING_CLASS,
  Confirmed: 'bg-secondary [&_span]:text-white!',
  Active: 'bg-secondary [&_span]:text-white!',
  'Not Requested': 'bg-grey-lightest [&_span]:text-black!',
  Unavailable: 'bg-grey-lightest [&_span]:text-black!'
};

function CarrierCard({
  name,
  type,
  carrierIcon,
  carrierIconColor,
  active,
  setActive = () => {},
  agencyDisabled = false,
  showOepIcon = false,
  className
}) {
  const isActive = active === name;
  const user = useSelector(state => state.user.zohoData);
  const { requestedAppointmentsByCarrier, actionableAppointments } =
    useSelector(state => state.appointments);

  const carrierReqAppts = requestedAppointmentsByCarrier[name] ?? [];
  const isAgentAppointed = isAgentAppointedWithCarrier(name, user);
  const hasPendingAppts = carrierReqAppts.length > 0;
  const hasActionableAppts = actionableAppointments.some(
    a => a.carrier === name
  );

  let status = 'Not Requested';
  if (!agencyDisabled) {
    const suncareStatus = getSuncareCarrierCardStatus(user, name);

    if (suncareStatus) {
      status = suncareStatus;
    } else if (hasActionableAppts) {
      status = 'Action Needed';
    } else if (hasPendingAppts) {
      status = 'Pending';
    } else if (isAgentAppointed) {
      status = company === 'oneill' ? 'Confirmed' : 'Active';
    }
  }

  const statusClassName = isActive
    ? 'bg-primary-emphasis-light [&_span]:text-white!'
    : STATUS_CLASS[status] ?? STATUS_CLASS['Not Requested'];

  return (
    <div
      className={cn(
        'flex min-h-[90px] h-full w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg px-4 py-2 transition-all duration-100 select-none hover:brightness-80',
        statusClassName,
        className
      )}
      onClick={() => setActive({ name, type })}
    >
      <div className='flex w-full flex-col items-center justify-center gap-1'>
        <div className='flex items-center justify-center gap-1.5'>
          {agencyDisabled && (
            <ShieldOff
              size={CARRIER_ICON_SIZE}
              className='shrink-0 text-semantic-danger'
              aria-hidden
            />
          )}
          <CarrierLucideIcon
            name={carrierIcon}
            color={carrierIconColor}
            size={CARRIER_ICON_SIZE}
            whiteIcon={isActive || status !== 'Not Requested'}
          />
          {showOepIcon && (
            <span
              className='flex shrink-0 items-center'
              title={OEP_UPDATE_ICON_DESCRIPTION}
            >
              <Megaphone
                size={CARRIER_ICON_SIZE}
                style={{
                  color:
                    isActive || status !== 'Not Requested'
                      ? '#fff'
                      : OEP_UPDATE_ICON_COLOR
                }}
                aria-label={OEP_UPDATE_ICON_DESCRIPTION}
              />
            </span>
          )}
        </div>
        <span className='text-md w-full text-center font-medium wrap-break-word'>
          {name}
        </span>
      </div>
    </div>
  );
}

CarrierCard.propTypes = {
  name: propTypes.string,
  carrierIcon: propTypes.string,
  carrierIconColor: propTypes.string,
  active: propTypes.string,
  type: propTypes.string,
  setActive: propTypes.func,
  agencyDisabled: propTypes.bool,
  showOepIcon: propTypes.bool,
  className: propTypes.string
};

export default CarrierCard;
